import React, { useState } from 'react';
import { collection, getDocs, query, where, updateDoc, doc, arrayUnion } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { useUser } from '../context/UserContext';

interface LinkingResult {
  propiedad_id: string;
  list_number: string;
  agente_listador: string;
  asesor_encontrado: string | null;
  vinculado: boolean;
  error?: string;
}

export default function PropertyLinker() {
  const { user } = useUser();
  const [isRunning, setIsRunning] = useState(false);
  const [logs, setLogs] = useState<string[]>([]);
  const [results, setResults] = useState<LinkingResult[]>([]);
  const [stats, setStats] = useState<any>(null);

  // Solo admins pueden acceder
  if (!user || !user.tipo?.includes('admin')) {
    return (
      <div className="container mt-5">
        <div className="alert alert-danger">
          <h4>Acceso Denegado</h4>
          <p>Solo administradores pueden ejecutar la vinculación de propiedades.</p>
        </div>
      </div>
    );
  }

  const addLog = (message: string) => {
    setLogs(prev => [...prev, `${new Date().toLocaleTimeString()}: ${message}`]);
    console.log(message);
  };

  const findAsesorByName = async (nombreCompleto: string): Promise<{ asesor_id: string; nombre: string } | null> => {
    try {
      // Buscar por nombre exacto primero
      const exactQuery = query(collection(db, 'asesor'), where('nombre', '==', nombreCompleto.trim()));
      const exactSnapshot = await getDocs(exactQuery);
      
      if (!exactSnapshot.empty) {
        const data = exactSnapshot.docs[0].data();
        return {
          asesor_id: data.asesor_id,
          nombre: data.nombre
        };
      }

      // Buscar fuzzy - obtener todos los asesores y buscar coincidencias
      const allAsesores = await getDocs(collection(db, 'asesor'));
      const asesoresData = allAsesores.docs.map(doc => ({ 
        asesor_id: doc.data().asesor_id,
        nombre: doc.data().nombre 
      }));

      const nombreBuscado = nombreCompleto.toLowerCase().trim();
      
      // Intentar diferentes estrategias de matching
      for (const asesor of asesoresData) {
        const nombreAsesor = asesor.nombre.toLowerCase();
        
        // 1. Coincidencia parcial en ambas direcciones
        if (nombreAsesor.includes(nombreBuscado) || nombreBuscado.includes(nombreAsesor)) {
          return asesor;
        }
        
        // 2. Coincidencia por palabras clave (nombre + apellido)
        const palabrasCSV = nombreBuscado.split(' ').filter((p: string) => p.length > 2);
        const palabrasAsesor = nombreAsesor.split(' ').filter((p: string) => p.length > 2);
        
        const coincidencias = palabrasCSV.filter((palabra: string) => 
          palabrasAsesor.some((palabraAsesor: string) => 
            palabraAsesor.includes(palabra) || palabra.includes(palabraAsesor)
          )
        );
        
        // Si al menos 2 palabras coinciden, considerarlo match
        if (coincidencias.length >= 2) {
          return asesor;
        }
      }

      return null;
    } catch (error) {
      console.error(`Error buscando asesor "${nombreCompleto}":`, error);
      return null;
    }
  };

  const ejecutarVinculacion = async () => {
    setIsRunning(true);
    setLogs([]);
    setResults([]);
    setStats(null);

    try {
      addLog('🚀 Iniciando vinculación de propiedades con asesores...');
      
      // Obtener todas las propiedades que tienen agente_listador pero no asesor_id
      const propiedadesQuery = query(collection(db, 'propiedades'));
      const propiedadesSnap = await getDocs(propiedadesQuery);
      
      const propiedadesSinAsesor = propiedadesSnap.docs.filter(doc => {
        const data = doc.data();
        return data.agencia_info?.agente_listador && 
               (!data.asesor_id || data.asesor_id.length === 0);
      });
      
      addLog(`📊 Encontradas ${propiedadesSinAsesor.length} propiedades sin asesor asignado de ${propiedadesSnap.size} totales`);
      
      const resultados: LinkingResult[] = [];
      let vinculadas = 0;
      let noEncontradas = 0;
      let errores = 0;

      for (const propDoc of propiedadesSinAsesor) {
        const propData = propDoc.data();
        const agenteListador = propData.agencia_info.agente_listador;
        
        try {
          addLog(`🔍 Buscando asesor para: "${agenteListador}"`);
          
          const asesorEncontrado = await findAsesorByName(agenteListador);
          
          if (asesorEncontrado) {
            // Actualizar la propiedad con el asesor_id
            await updateDoc(doc(db, 'propiedades', propDoc.id), {
              asesor_id: [asesorEncontrado.asesor_id]
            });
            
            // Actualizar el asesor con esta propiedad
            const asesorQuery = query(collection(db, 'asesor'), where('asesor_id', '==', asesorEncontrado.asesor_id));
            const asesorSnap = await getDocs(asesorQuery);
            
            if (!asesorSnap.empty) {
              await updateDoc(asesorSnap.docs[0].ref, {
                propiedad_id: arrayUnion(propData.propiedad_id)
              });
            }
            
            resultados.push({
              propiedad_id: propData.propiedad_id,
              list_number: propData.list_number,
              agente_listador: agenteListador,
              asesor_encontrado: asesorEncontrado.asesor_id,
              vinculado: true
            });
            
            vinculadas++;
            addLog(`✅ Vinculada: ${propData.list_number} → ${asesorEncontrado.nombre} (${asesorEncontrado.asesor_id})`);
            
          } else {
            resultados.push({
              propiedad_id: propData.propiedad_id,
              list_number: propData.list_number,
              agente_listador: agenteListador,
              asesor_encontrado: null,
              vinculado: false,
              error: 'Asesor no encontrado'
            });
            
            noEncontradas++;
            addLog(`⚠️ No encontrado: ${propData.list_number} → "${agenteListador}"`);
          }
          
        } catch (error: any) {
          resultados.push({
            propiedad_id: propData.propiedad_id,
            list_number: propData.list_number,
            agente_listador: agenteListador,
            asesor_encontrado: null,
            vinculado: false,
            error: error.message
          });
          
          errores++;
          addLog(`❌ Error: ${propData.list_number} → ${error.message}`);
        }
      }

      setResults(resultados);
      setStats({
        total: propiedadesSinAsesor.length,
        vinculadas,
        noEncontradas,
        errores
      });
      
      addLog(`🎉 Vinculación completada: ${vinculadas} exitosas, ${noEncontradas} no encontradas, ${errores} errores`);
      
    } catch (error: any) {
      addLog(`💥 Error crítico: ${error.message}`);
    } finally {
      setIsRunning(false);
    }
  };

  return (
    <div className="container mt-4">
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-header bg-warning text-white">
              <h3 className="mb-0">🔗 Vinculador de Propiedades con Asesores</h3>
              <small>Corrección post-migración para vincular propiedades con asesores usando nombres del CSV</small>
            </div>
            <div className="card-body">
              
              <div className="alert alert-info">
                <h6>🔍 Problema Detectado</h6>
                <p>
                  Las propiedades tienen <code>agencia_info.agente_listador</code> (nombres del CSV) 
                  pero no tienen <code>asesor_id[]</code> asignados porque el matching durante la migración falló.
                </p>
                <p className="mb-0">
                  Este script buscará asesores por nombre y creará las vinculaciones faltantes.
                </p>
              </div>

              {/* Botón de ejecución */}
              <div className="text-center mb-4">
                <button
                  className={`btn btn-lg ${isRunning ? 'btn-warning' : 'btn-danger'}`}
                  onClick={ejecutarVinculacion}
                  disabled={isRunning}
                >
                  {isRunning ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Ejecutando Vinculación...
                    </>
                  ) : (
                    <>
                      🔗 EJECUTAR VINCULACIÓN DE ASESORES
                    </>
                  )}
                </button>
                {isRunning && (
                  <div className="mt-2">
                    <small className="text-warning">
                      ⚠️ No cierre esta ventana durante el proceso
                    </small>
                  </div>
                )}
              </div>

              {/* Logs */}
              {logs.length > 0 && (
                <div className="card mb-4">
                  <div className="card-header d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">📋 Logs de Vinculación</h5>
                    <span className="badge bg-secondary">{logs.length} entradas</span>
                  </div>
                  <div className="card-body" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    {logs.map((log, index) => (
                      <div key={index} className="font-monospace small mb-1 p-1 border-start border-2 border-info">
                        {log}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Estadísticas */}
              {stats && (
                <div className="row mb-4">
                  <div className="col-md-3">
                    <div className="card text-center border-info">
                      <div className="card-body">
                        <h4 className="text-info">{stats.total}</h4>
                        <small>Propiedades procesadas</small>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="card text-center border-success">
                      <div className="card-body">
                        <h4 className="text-success">{stats.vinculadas}</h4>
                        <small>Vinculadas exitosamente</small>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="card text-center border-warning">
                      <div className="card-body">
                        <h4 className="text-warning">{stats.noEncontradas}</h4>
                        <small>Asesores no encontrados</small>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="card text-center border-danger">
                      <div className="card-body">
                        <h4 className="text-danger">{stats.errores}</h4>
                        <small>Errores</small>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Resultados detallados */}
              {results.length > 0 && (
                <div className="card">
                  <div className="card-header bg-secondary text-white">
                    <h5 className="mb-0">📊 Resultados Detallados</h5>
                  </div>
                  <div className="card-body" style={{ maxHeight: '600px', overflowY: 'auto' }}>
                    <div className="table-responsive">
                      <table className="table table-sm">
                        <thead>
                          <tr>
                            <th>Propiedad</th>
                            <th>Agente Listador (CSV)</th>
                            <th>Asesor Encontrado</th>
                            <th>Estado</th>
                          </tr>
                        </thead>
                        <tbody>
                          {results.map((result, i) => (
                            <tr key={i} className={result.vinculado ? 'table-success' : 'table-warning'}>
                              <td>{result.list_number}</td>
                              <td>{result.agente_listador}</td>
                              <td>{result.asesor_encontrado || 'No encontrado'}</td>
                              <td>
                                {result.vinculado ? (
                                  <span className="badge bg-success">✅ Vinculado</span>
                                ) : (
                                  <span className="badge bg-warning">⚠️ Sin vincular</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {results.length > 0 && (
                <div className="alert alert-success mt-4">
                  <h6>🎉 Proceso Completado</h6>
                  <p className="mb-0">
                    Después de ejecutar la vinculación, ve a cualquier página de asesor para verificar 
                    que ahora aparezcan sus propiedades asignadas.
                  </p>
                </div>
              )}

            </div>
          </div>
        </div>
      </div>
    </div>
  );
}